import React, { useEffect, useMemo, useRef, useState } from 'react';
import { palette, status } from '../../../../style-utils/theme';
import { Text } from '../../../../style-utils/text';
import {
  InlineMessage,
  Shake,
  Staleable,
  StatusLabel,
} from '../../../../motion';
import {
  BannerIcon,
  Field,
  FieldGrid,
  FormActions,
  FormCard,
  FormFieldset,
  GhostButton,
  HelpText,
  InlineError,
  Label,
  OrgWideBanner,
  PrimaryButton,
  SectionEyebrow,
  StyledSelect,
  ToggleCopy,
  ToggleRow,
  ToggleTitle,
} from './dashboard-scopes.styled';
import {
  CAPABILITY_COPY,
  GRANT_REASON_LABELS,
  SCOPE_TYPE_DESCRIPTIONS,
  SCOPE_TYPE_LABELS,
} from './scope-vocabulary';
import {
  AccessGrantCreated,
  CreateAccessGrantInput,
  GRANT_REASONS,
  GrantReason,
  Portfolio,
  SCOPE_TYPES,
  ScopeType,
  UnitListItem,
} from '../../../../interfaces/hierarchy-interfaces';
import {
  HierarchyApiError,
  grantStaffScope,
  listPortfolioBuildings,
  listPortfolios,
  listUnits,
} from '../../../../api/hierarchy-client';
import { useAuth } from '../../../../context/auth-provider';
import { ROLES, normalizeRole } from '../../../../auth/roles';

interface BuildingOption {
  id: number;
  name: string;
  // Kept alongside the name because the grouping key is the id, not the label —
  // see buildingsByPortfolio.
  portfolioId: number;
  portfolioName: string;
  portfolioIsDefault: boolean;
}

// One <optgroup> in the building picker: the heading plus the buildings under
// it. The id rides along so two portfolios that ended up with the same name
// still render as two groups with distinct React keys.
interface PortfolioGroup {
  id: number;
  name: string;
  isDefault: boolean;
  options: BuildingOption[];
}

interface DashboardAddGrantProps {
  userId: string;
  staffName: string;
  onGranted: (created: AccessGrantCreated) => void;
  onCancel: () => void;
}

const GRANT_REASON_HELP: Record<GrantReason, string> = {
  assignment: 'Their standing responsibility.',
  coverage: 'Standing in for someone else.',
  training: 'Shadowing or being onboarded.',
  temporary: 'Short-term, expected to be revoked.',
};

export const DashboardAddGrant: React.FC<DashboardAddGrantProps> = ({
  userId,
  staffName,
  onGranted,
  onCancel,
}) => {
  const { user } = useAuth();
  // can_administer is owner-only on the server, enforced separately from the
  // scope:manage permission. Reflecting that here keeps a property manager from
  // filling in a form the API will reject.
  const viewerIsOwner = normalizeRole(user?.role) === ROLES.OWNER;

  const [scopeType, setScopeType] = useState<ScopeType>('building');
  const [portfolioId, setPortfolioId] = useState<number | null>(null);
  const [buildingId, setBuildingId] = useState<number | null>(null);
  const [unitId, setUnitId] = useState<number | null>(null);
  const [canOperate, setCanOperate] = useState<boolean>(true);
  const [canAdminister, setCanAdminister] = useState<boolean>(false);
  const [reason, setReason] = useState<GrantReason>('assignment');

  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [buildings, setBuildings] = useState<BuildingOption[]>([]);
  const [units, setUnits] = useState<UnitListItem[]>([]);
  // One flag PER list, not one shared between them. The two loaders each have
  // their own already-loaded short-circuit, so a shared flag can be left stuck:
  // switch to Portfolio, then back to Building before the portfolio fetch
  // resolves, and the portfolio effect's cleanup skips its `finally` while the
  // building effect returns early without entering load() — nothing ever clears
  // it, and the building select stays disabled on "Loading buildings…" forever.
  const [loadingPortfolios, setLoadingPortfolios] = useState<boolean>(false);
  const [loadingBuildings, setLoadingBuildings] = useState<boolean>(false);
  const [loadingUnits, setLoadingUnits] = useState<boolean>(false);

  const [submitting, setSubmitting] = useState<boolean>(false);
  const submitRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [isDuplicate, setIsDuplicate] = useState<boolean>(false);
  // Counts refused submits, so the error shakes again on the next one.
  const [attempt, setAttempt] = useState(0);

  const needsPortfolioList = scopeType === 'portfolio';
  const needsBuildingList = scopeType === 'building' || scopeType === 'unit';

  // Only the portfolio picker needs this list. The building options carry the
  // portfolio they sit in, so a building or unit grant never pays for it.
  //
  // listPortfolios() with no argument is ACTIVE portfolios only, and this client
  // filter is the ONLY guard there is — do not widen it to 'all'.
  // staff-scopes.js validates org membership and nothing else, and
  // lb_expand_grant never inspects portfolios.status, so a grant pointed at an
  // archived portfolio is accepted with a 200 and silently expanded into
  // access_scope. Offering one here would hand out access through a portfolio
  // the owner believes they put away.
  //
  // The building index below may still name an archived portfolio as a group
  // heading — that is a real building someone can legitimately be granted, and
  // it is a different question from which portfolios are grantable.
  useEffect(() => {
    let cancelled = false;

    if (!needsPortfolioList) {
      return undefined;
    }

    if (portfolios.length > 0) {
      return undefined;
    }

    const load = async () => {
      setLoadingPortfolios(true);
      setError(null);

      try {
        const rows = await listPortfolios();

        if (!cancelled) {
          setPortfolios(rows);
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(
            loadError instanceof HierarchyApiError
              ? loadError.message
              : 'Could not load portfolios.'
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingPortfolios(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [needsPortfolioList, portfolios.length]);

  // Every building in the organization in ONE request, each row already stamped
  // with the portfolio holding it. This used to be a getPortfolio() per
  // portfolio — one request each, purely to learn that mapping.
  useEffect(() => {
    let cancelled = false;

    if (!needsBuildingList) {
      return undefined;
    }

    if (buildings.length > 0) {
      return undefined;
    }

    const load = async () => {
      setLoadingBuildings(true);
      setError(null);

      try {
        const rows = await listPortfolioBuildings();

        const options = rows.map(row => ({
          id: row.id,
          name: row.property_name,
          portfolioId: row.portfolio_id,
          // The index covers buildings in ARCHIVED portfolios too, and resolves
          // the name from the org's full portfolio set — so an archived
          // portfolio still names its own group. The id fallback catches the
          // one remaining case (the portfolio row itself could not be
          // resolved), because an <optgroup> with a blank label renders as an
          // unexplained gap in the list.
          portfolioName: row.portfolio_name || `Portfolio #${row.portfolio_id}`,
          portfolioIsDefault: row.portfolio_is_default,
        }));

        if (!cancelled) {
          setBuildings(options);
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(
            loadError instanceof HierarchyApiError
              ? loadError.message
              : 'Could not load buildings.'
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingBuildings(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [needsBuildingList, buildings.length]);

  // A unit grant needs its building chosen first, so the unit list is scoped
  // rather than being every unit in the organization.
  useEffect(() => {
    let cancelled = false;

    if (scopeType !== 'unit' || buildingId === null) {
      setUnits([]);

      return undefined;
    }

    const load = async () => {
      setLoadingUnits(true);
      setUnitId(null);

      try {
        const rows = await listUnits(buildingId);

        if (!cancelled) {
          setUnits(rows);
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(
            loadError instanceof HierarchyApiError
              ? loadError.message
              : 'Could not load units.'
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingUnits(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [scopeType, buildingId]);

  // Keyed on the portfolio id, not its name: uq_pf_name is partial on
  // status='active', so an archived portfolio may share a name with a live one
  // and grouping by label would fold two real portfolios into one heading.
  const buildingsByPortfolio = useMemo(() => {
    const groups = new Map<number, PortfolioGroup>();

    buildings.forEach(building => {
      const existing = groups.get(building.portfolioId);

      if (existing) {
        existing.options.push(building);

        return;
      }

      groups.set(building.portfolioId, {
        id: building.portfolioId,
        name: building.portfolioName,
        isDefault: building.portfolioIsDefault,
        options: [building],
      });
    });

    // The index is sorted by building name across the whole organization, so a
    // portfolio's rows are not contiguous and first-appearance order would be
    // arbitrary.
    //
    // Default portfolio first, then by name — the same precedence GET
    // /portfolios returns (is_default DESC, name ASC). The default is where
    // every building lands when no portfolio is named, so it is usually the
    // biggest group and the one being looked for.
    return Array.from(groups.values()).sort((first, second) => {
      if (first.isDefault !== second.isDefault) {
        return first.isDefault ? -1 : 1;
      }

      return first.name.localeCompare(second.name);
    });
  }, [buildings]);

  const handleScopeTypeChange = (nextScopeType: ScopeType) => {
    setScopeType(nextScopeType);
    // The target ids are mutually exclusive on the server (sending an extra one
    // is a 400), so switching type clears every previous selection.
    setPortfolioId(null);
    setBuildingId(null);
    setUnitId(null);
    setError(null);
    setIsDuplicate(false);
  };

  const targetChosen =
    scopeType === 'organization' ||
    (scopeType === 'portfolio' && portfolioId !== null) ||
    (scopeType === 'building' && buildingId !== null) ||
    (scopeType === 'unit' && unitId !== null);

  // A grant with neither capability is a row that grants nothing — block it
  // here rather than letting someone create silent dead access.
  const hasCapability = canOperate || canAdminister;
  const canSubmit = targetChosen && hasCapability && !submitting;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!canSubmit || submitRef.current) {
      return;
    }

    submitRef.current = true;
    setSubmitting(true);
    setError(null);
    setIsDuplicate(false);

    const input: CreateAccessGrantInput = {
      scope_type: scopeType,
      can_operate: canOperate,
      can_administer: canAdminister,
      reason,
    };

    if (scopeType === 'portfolio' && portfolioId !== null) {
      input.portfolio_id = portfolioId;
    }

    if (scopeType === 'building' && buildingId !== null) {
      input.building_id = buildingId;
    }

    if (scopeType === 'unit' && unitId !== null) {
      input.unit_id = unitId;
    }

    try {
      const created = await grantStaffScope(userId, input);

      submitRef.current = false;
      onGranted(created);
    } catch (submitError) {
      if (submitError instanceof HierarchyApiError) {
        setIsDuplicate(submitError.conflict === 'grant_already_open');
        setError(submitError.message);
      } else {
        setError('Could not create the grant. Please try again.');
      }

      setAttempt(count => count + 1);
      submitRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <FormCard onSubmit={handleSubmit} aria-busy={submitting || undefined}>
      <div>
        <SectionEyebrow>New grant</SectionEyebrow>
        <Text mt={4} mb={0} fontSize={18} fontWeight={700} color={palette.ink}>
          Give {staffName} access
        </Text>
      </div>

      {/* SUBMITTING KEEPS THE FORM ON SCREEN: it dims (Staleable), goes inert
          (the fieldset is disabled, aria-busy says so) and the button carries
          the pending state at a stable width. */}
      <Staleable stale={submitting}>
      <FormFieldset disabled={submitting}>
      <FieldGrid>
        <Field>
          <Label htmlFor="grant-scope-type">What should this cover?</Label>
          <StyledSelect
            id="grant-scope-type"
            value={scopeType}
            onChange={event =>
              handleScopeTypeChange(event.target.value as ScopeType)
            }
          >
            {SCOPE_TYPES.map(type => (
              <option key={type} value={type}>
                {SCOPE_TYPE_LABELS[type]}
              </option>
            ))}
          </StyledSelect>
          <HelpText>{SCOPE_TYPE_DESCRIPTIONS[scopeType]}</HelpText>
        </Field>

        <Field>
          <Label htmlFor="grant-reason">Why?</Label>
          <StyledSelect
            id="grant-reason"
            value={reason}
            onChange={event => setReason(event.target.value as GrantReason)}
          >
            {GRANT_REASONS.map(value => (
              <option key={value} value={value}>
                {GRANT_REASON_LABELS[value]}
              </option>
            ))}
          </StyledSelect>
          <HelpText>{GRANT_REASON_HELP[reason]}</HelpText>
        </Field>
      </FieldGrid>

      {/* The single most consequential thing an admin can do on this screen,
          so it gets a callout rather than a line of help text. */}
      {scopeType === 'organization' && (
        <OrgWideBanner>
          <BannerIcon>&#127758;</BannerIcon>
          <div>
            <Text
              mt={0}
              mb={0}
              fontSize={14}
              fontWeight={600}
              color={palette.irisDark}
            >
              This covers every building — now and in the future
            </Text>
            <Text mt={4} mb={0} fontSize={14} color={palette.slate}>
              {staffName} will keep access automatically when a new building is
              added to the organisation. Nobody has to remember to update this
              grant. Choose a portfolio or building instead if that is not what
              you want.
            </Text>
          </div>
        </OrgWideBanner>
      )}

      {scopeType === 'portfolio' && (
        <Field>
          <Label htmlFor="grant-portfolio">Which portfolio?</Label>
          <StyledSelect
            id="grant-portfolio"
            value={portfolioId === null ? '' : String(portfolioId)}
            disabled={loadingPortfolios}
            onChange={event =>
              setPortfolioId(
                event.target.value === '' ? null : Number(event.target.value)
              )
            }
          >
            <option value="">
              {loadingPortfolios
                ? 'Loading portfolios…'
                : 'Select a portfolio…'}
            </option>
            {portfolios.map(portfolio => (
              <option key={portfolio.id} value={portfolio.id}>
                {portfolio.name} ({portfolio.building_count}{' '}
                {portfolio.building_count === 1 ? 'building' : 'buildings'})
              </option>
            ))}
          </StyledSelect>
          <HelpText>
            Buildings later added to this portfolio are included automatically.
          </HelpText>
        </Field>
      )}

      {needsBuildingList && (
        <Field>
          <Label htmlFor="grant-building">
            {scopeType === 'unit'
              ? 'Which building is the unit in?'
              : 'Which building?'}
          </Label>
          <StyledSelect
            id="grant-building"
            value={buildingId === null ? '' : String(buildingId)}
            disabled={loadingBuildings}
            onChange={event =>
              setBuildingId(
                event.target.value === '' ? null : Number(event.target.value)
              )
            }
          >
            <option value="">
              {loadingBuildings ? 'Loading buildings…' : 'Select a building…'}
            </option>
            {buildingsByPortfolio.map(group => (
              <optgroup key={group.id} label={group.name}>
                {group.options.map(building => (
                  <option key={building.id} value={building.id}>
                    {building.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </StyledSelect>
        </Field>
      )}

      {scopeType === 'unit' && (
        <Field>
          <Label htmlFor="grant-unit">Which unit?</Label>
          <StyledSelect
            id="grant-unit"
            value={unitId === null ? '' : String(unitId)}
            disabled={buildingId === null || loadingUnits}
            onChange={event =>
              setUnitId(
                event.target.value === '' ? null : Number(event.target.value)
              )
            }
          >
            <option value="">
              {buildingId === null
                ? 'Choose a building first…'
                : loadingUnits
                ? 'Loading units…'
                : 'Select a unit…'}
            </option>
            {units.map(unit => (
              <option key={unit.id} value={unit.id}>
                {unit.unit_number}
                {unit.kind === 'unassigned' ? ' (unassigned placeholder)' : ''}
              </option>
            ))}
          </StyledSelect>
          {buildingId !== null && !loadingUnits && units.length === 0 && (
            <HelpText>This building has no units yet.</HelpText>
          )}
        </Field>
      )}

      <Field>
        <Label>What can they do inside it?</Label>

        <ToggleRow htmlFor="grant-can-operate">
          <input
            id="grant-can-operate"
            type="checkbox"
            checked={canOperate}
            onChange={event => setCanOperate(event.target.checked)}
          />
          <ToggleCopy>
            <ToggleTitle>{CAPABILITY_COPY.operate.title}</ToggleTitle>
            <HelpText>{CAPABILITY_COPY.operate.help}</HelpText>
          </ToggleCopy>
        </ToggleRow>

        <ToggleRow htmlFor="grant-can-administer" isDisabled={!viewerIsOwner}>
          <input
            id="grant-can-administer"
            type="checkbox"
            checked={canAdminister}
            disabled={!viewerIsOwner}
            onChange={event => setCanAdminister(event.target.checked)}
          />
          <ToggleCopy>
            <ToggleTitle>{CAPABILITY_COPY.administer.title}</ToggleTitle>
            <HelpText>{CAPABILITY_COPY.administer.help}</HelpText>
            {!viewerIsOwner && (
              <HelpText>
                Only an owner can hand out administrative control, so this is
                unavailable to you.
              </HelpText>
            )}
          </ToggleCopy>
        </ToggleRow>

        {!hasCapability && (
          <HelpText>
            Turn on at least one — a grant with neither gives no access at all.
          </HelpText>
        )}
      </Field>
      </FormFieldset>
      </Staleable>

      {/* The refusal unfolds (InlineMessage) and shakes once per attempt. */}
      <InlineMessage
        role="alert"
        message={
          error ? (
            <Shake active attempt={attempt}>
              <InlineError>
                <span aria-hidden="true">&#9888;</span>
                <div>
                  <Text
                    mt={0}
                    mb={0}
                    fontSize={14}
                    fontWeight={600}
                    color={status.alert.fg}
                  >
                    {isDuplicate
                      ? 'They already have this'
                      : 'Could not create the grant'}
                  </Text>
                  <Text mt={4} mb={0} fontSize={14} color={status.alert.fg}>
                    {error}
                  </Text>
                  {isDuplicate && (
                    <Text mt={4} mb={0} fontSize={14} color={status.alert.fg}>
                      The matching grant is already listed under Grants below.
                      Revoke it first if you want to change what it allows.
                    </Text>
                  )}
                </div>
              </InlineError>
            </Shake>
          ) : undefined
        }
      />

      <FormActions>
        <GhostButton type="button" onClick={onCancel} disabled={submitting}>
          Cancel
        </GhostButton>
        <PrimaryButton type="submit" disabled={!canSubmit}>
          <StatusLabel
            state={submitting ? 'busy' : 'idle'}
            labels={{ idle: 'Grant access', busy: 'Granting…' }}
          />
        </PrimaryButton>
      </FormActions>
    </FormCard>
  );
};
